import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function PrivacyPolicyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Privacy Policy | ProtectWize Data Protection & Security',
    description: 'Read ProtectWize\'s privacy policy. Learn how we protect your data, handle personal information, and maintain enterprise-grade security standards.',
    keywords: 'privacy policy, data protection, security policy, personal information, data privacy',
    canonical: `${siteUrl}/privacy-policy`,
    ogType: 'website',
    robots: 'index, follow',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Privacy Policy',
      description: 'ProtectWize Privacy Policy - Data Protection and Security Standards',
      url: `${siteUrl}/privacy-policy`,
      publisher: {
        '@type': 'Organization',
        name: 'ProtectWize'
      },
      dateModified: new Date().toISOString().split('T')[0]
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
              Privacy Policy
            </h1>
            <p className="text-gray-400 text-lg">
              Last updated: January 2024
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Introduction
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                At ProtectWize, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our analytics platform and services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Information We Collect
              </h2>
              
              <h3 className="text-2xl font-semibold text-white mb-3 mt-6">Personal Information</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you register on the platform, express an interest in obtaining information about us or our products and services, or otherwise contact us. The personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li>Name and contact information (email address, phone number)</li>
                <li>Company name and business information</li>
                <li>Account credentials (username and password)</li>
                <li>Payment and billing information</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-2xl font-semibold text-white mb-3 mt-6">Usage Data</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We automatically collect certain information when you visit, use, or navigate our platform. This information does not reveal your specific identity but may include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li>Device and browser information</li>
                <li>IP address and location data</li>
                <li>Usage patterns and preferences</li>
                <li>Analytics and performance metrics</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                How We Use Your Information
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the information we collect or receive to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li>Provide, operate, and maintain our services</li>
                <li>Process your transactions and manage your account</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments, questions, and customer service requests</li>
                <li>Improve and personalize your experience on our platform</li>
                <li>Develop new products, services, and features</li>
                <li>Monitor and analyze usage trends and activities</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations and enforce our terms and policies</li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Data Security
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, please note that no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Our security measures include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li>End-to-end encryption for data transmission</li>
                <li>Regular security audits and penetration testing</li>
                <li>Multi-factor authentication options</li>
                <li>Secure data centers with 24/7 monitoring</li>
                <li>Employee training on data protection and privacy</li>
                <li>Incident response and breach notification procedures</li>
              </ul>
            </section>

            {/* Data Sharing and Disclosure */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Data Sharing and Disclosure
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li><strong className="text-white">Service Providers:</strong> We may share your information with third-party vendors who perform services on our behalf, such as payment processing, data analysis, and customer support.</li>
                <li><strong className="text-white">Business Transfers:</strong> We may share or transfer your information in connection with a merger, acquisition, or sale of assets.</li>
                <li><strong className="text-white">Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                <li><strong className="text-white">With Your Consent:</strong> We may disclose your information for any other purpose with your consent.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                We do not sell your personal information to third parties for their marketing purposes.
              </p>
            </section>

            {/* Your Privacy Rights */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Your Privacy Rights
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li><strong className="text-white">Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information</li>
                <li><strong className="text-white">Portability:</strong> Request a copy of your data in a structured format</li>
                <li><strong className="text-white">Objection:</strong> Object to our processing of your personal information</li>
                <li><strong className="text-white">Restriction:</strong> Request restriction of processing your information</li>
                <li><strong className="text-white">Withdraw Consent:</strong> Withdraw your consent at any time</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                To exercise these rights, please contact us using the information provided below. We will respond to your request within 30 days.
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our platform and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                Types of cookies we use:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li><strong className="text-white">Essential Cookies:</strong> Required for the platform to function properly</li>
                <li><strong className="text-white">Analytics Cookies:</strong> Help us understand how visitors interact with our platform</li>
                <li><strong className="text-white">Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong className="text-white">Marketing Cookies:</strong> Track your activity to deliver relevant advertisements</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Data Retention
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Retention periods vary depending on the type of information and the purpose for which it was collected, typically ranging from 3 to 7 years for business and legal compliance purposes.
              </p>
            </section>

            {/* International Data Transfers */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                International Data Transfers
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Children's Privacy
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.
              </p>
            </section>

            {/* Contact Us */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                Contact Us
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have questions or comments about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gray-900 border border-gray-800 p-6 rounded-lg">
                <p className="text-white font-semibold mb-2">ProtectWize Privacy Team</p>
                <p className="text-gray-300">Email: privacy@protectwize.com</p>
              </div>
            </section>

            {/* GDPR Compliance */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                GDPR Compliance
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you are a resident of the European Economic Area (EEA), you have certain data protection rights under the General Data Protection Regulation (GDPR). ProtectWize aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your personal information.
              </p>
              <p className="text-gray-300 leading-relaxed">
                We process your personal information based on the following legal grounds: consent, contract performance, legal obligations, and legitimate interests. You have the right to lodge a complaint with a supervisory authority if you believe our processing of your information violates applicable law.
              </p>
            </section>

            {/* CCPA Privacy Rights */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                CCPA Privacy Rights (California Residents)
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you are a California resident, you have specific rights regarding your personal information under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
                <li>Right to know what personal information is collected, used, shared, or sold</li>
                <li>Right to delete personal information held by businesses</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising CCPA rights</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                To exercise these rights, please submit a verifiable consumer request to us using the contact information provided above.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}